import {useEffect} from "react";
import {Navigate, useLocation, useParams} from "react-router-dom";
import {useAtom} from "jotai";
import {authStatusAtom, tokenResultAtom} from "../../store";
import {refresh, refreshAdmin} from "../../services/auth/AuthAxios";
import {canAccessBoardRoute} from "../../routing/boardRoutes";

const getResponseStatus = (response) =>
  response?.responseCode?.statusCode ?? response?.statusCode;

const toAdminSession = (data) => ({
  id: data.email,
  role: data.role,
  name: data.name,
  accessToken: data.token.accessToken,
  refreshToken: data.token.refreshToken,
  username: '',
  serverName: '',
});

const toUserSession = (data) => ({
  id: data.id,
  username: data.username,
  role: data.role,
  name: data.name,
  accessToken: data.token.accessToken,
  refreshToken: data.token.refreshToken,
  serverName: '',
});

function ProtectedRoute({children}) {
  const {id} = useParams();
  const location = useLocation();
  const [authStatus, setAuthStatus] = useAtom(authStatusAtom);
  const [session, setSession] = useAtom(tokenResultAtom);

  useEffect(() => {
    let isActive = true;

    const restoreSession = async () => {
      if (session.role) {
        setAuthStatus('authenticated');
        return;
      }

      if (!localStorage.getItem('refreshToken')) {
        setAuthStatus('unauthenticated');
        return;
      }

      setAuthStatus('checking');

      try {
        const adminResponse = await refreshAdmin();
        if (getResponseStatus(adminResponse) === 200 && adminResponse?.data) {
          if (isActive) {
            setSession(toAdminSession(adminResponse.data));
            setAuthStatus('authenticated');
          }
          return;
        }

        const userResponse = await refresh();
        if (getResponseStatus(userResponse) === 200 && userResponse?.data) {
          if (isActive) {
            setSession(toUserSession(userResponse.data));
            setAuthStatus('authenticated');
          }
          return;
        }

        throw new Error('인증 정보를 복원할 수 없습니다.');
      } catch (error) {
        if (isActive) {
          localStorage.removeItem('refreshToken');
          setAuthStatus('unauthenticated');
        }
      }
    };

    restoreSession();

    return () => {
      isActive = false;
    };
  }, [session.role, setAuthStatus, setSession]);

  if (!session.role && (authStatus === 'idle' || authStatus === 'checking')) {
    return <RouteLoading role="status">인증 정보를 확인하고 있습니다.</RouteLoading>;
  }

  if (!session.role) {
    return <Navigate to="/" replace state={{from: location}}/>;
  }

  if (!canAccessBoardRoute(session.role, id)) {
    return <Navigate to="/board/dashboard" replace/>;
  }

  return children;
}

export default ProtectedRoute;

const RouteLoading = ({children, ...props}) => (
  <div
    {...props}
    style={{
      display: 'flex',
      minHeight: '100vh',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#666',
      fontSize: '14px',
    }}
  >
    {children}
  </div>
);
